import { Link, useNavigate } from "react-router";
import { BusinessLogo } from "@/design/BusinessLogo";
import { CardMapLink } from "@/design/CardMapLink";
import { FavoriteHeart } from "@/design/FavoriteHeart";
import { googleMapsHref } from "@/design/maps";
import { RatingBadge } from "@/design/Rating";
import {
  formatDistance,
  formatFromPrice,
  type BusinessCard,
} from "@/modules/discovery/surfaces";
import { put } from "~/lib/api";

/**
 * One business, as a card (R22 T12).
 *
 * The same tile on Search and on Favorites, which is why it is a component
 * rather than markup inside a screen: the two screens showing the same clinic
 * differently is the drift H35 explicitly avoided by having Favorites return
 * the *same* card the search returns.
 */
export function BusinessCardTile({
  card,
  favorite,
  signedIn,
  next,
  onFavoriteChange,
}: {
  card: BusinessCard;
  favorite: boolean;
  signedIn: boolean;
  /** Where a guest returns to after logging in from the heart. */
  next: string;
  /** Told after a successful write, so a list can drop a card it just lost. */
  onFavoriteChange?: (businessId: string, favorite: boolean) => void;
}) {
  const navigate = useNavigate();
  const fromPrice = formatFromPrice(card.minPriceCents);
  const practitionerHint =
    card.activePractitionerCount === 1
      ? "Solo practitioner"
      : card.activePractitionerCount > 1
        ? `${card.activePractitionerCount} practitioners`
        : null;

  return (
    <div className="relative h-full">
      {/* Outside the anchor on purpose: a button inside a link is invalid
          markup and browsers resolve it by dropping one of them. */}
      <FavoriteHeart
        businessId={card.id}
        initialFavorite={favorite}
        signedIn={signedIn}
        next={next}
        className="absolute end-3 top-3 z-10"
        push={(url) => void navigate(url)}
        save={async (businessId, wanted) => {
          // The desired state, never "flip it": two taps racing from two
          // devices settle on the same answer this way.
          const result = await put(`/favorites/${businessId}`, {
            favorite: wanted,
          });
          if (result.ok) onFavoriteChange?.(businessId, wanted);
          return result.ok;
        }}
      />
      <Link
        to={`/businesses/${card.id}`}
        className="lift flex h-full flex-col rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow-card)] hover:border-accent/40"
      >
        <div className="flex items-start gap-3">
          <BusinessLogo logoUrl={card.logoUrl} name={card.name} size={48} />
          <div className="min-w-0 flex-1 pe-10">
            <p className="truncate font-display font-bold text-ink">
              {card.name}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {card.categoryNames.map((name) => (
                <span
                  key={name}
                  className="rounded-lg bg-accent/12 px-2.5 py-0.5 text-xs font-semibold text-accent"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <RatingBadge summary={card.rating} />
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-sm text-muted">
          {fromPrice ? (
            <span className="font-mono font-semibold text-ink">{fromPrice}</span>
          ) : (
            <span>No services listed yet</span>
          )}
          {practitionerHint && <span>{practitionerHint}</span>}
          {/* Where it is, before how far it is. */}
          <CardMapLink href={googleMapsHref(card.latitude, card.longitude)} />
          {card.distanceMeters !== null && (
            <span>
              <span className="font-mono">
                {formatDistance(card.distanceMeters)}
              </span>{" "}
              away
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}
